import { type Static, Type as t } from "@sinclair/typebox";
import { GalleryCard, PolicyBlock, PolicyNone, PolicyWarn } from "./gallery";
import {
  BoolParam,
  CursorQuery,
  closed,
  Empty,
  Gid,
  GidParam,
  IsoDateTime,
  ListParam,
  literals,
  nullable,
  Ok,
  pageOf,
  Tag,
  Uuid,
  VIDEO_CODECS,
  VIDEO_STAGES,
  VIDEO_STATUSES,
} from "./primitives";

import { route, withIds } from "./route";

export const VideoStatus = literals(VIDEO_STATUSES);

export const VideoStage = literals(VIDEO_STAGES);

export const VideoCodec = literals(VIDEO_CODECS);

export const VideoPerson = closed({ name: t.String(), avatarUrl: nullable(t.String()) });

export const VideoProgress = closed({ positionMs: t.Integer({ minimum: 0 }), completed: t.Boolean() });

export const VideoState = closed({
  status: VideoStatus,
  stage: nullable(VideoStage),
  done: nullable(t.Integer({ minimum: 0 })),
  total: nullable(t.Integer({ minimum: 0 })),

  errorCode: nullable(t.String()),
  errorMessage: nullable(t.String()),
  removedReason: nullable(t.String()),
  queuePosition: nullable(t.Integer({ minimum: 1 })),
});

const cardProps = {
  id: Uuid,
  title: t.String(),
  uploader: VideoPerson,
  durationMs: nullable(t.Integer()),
  width: nullable(t.Integer()),
  height: nullable(t.Integer()),

  videoCodec: nullable(t.String()),
  viewCount: t.Integer({ minimum: 0 }),
  publishedAt: nullable(IsoDateTime),

  series: nullable(closed({ id: Uuid, title: t.String(), episodeNo: nullable(t.Integer()) })),
};

export const VideoCardOpen = closed({
  ...cardProps,
  policy: t.Union([PolicyNone, PolicyWarn]),
  poster: nullable(t.String()),
});

export const VideoCardBlocked = closed({ ...cardProps, policy: PolicyBlock });

export const VideoCard = t.Union([VideoCardOpen, VideoCardBlocked]);

export type VideoCardType = Static<typeof VideoCard>;

const detailProps = {
  nextVideos: t.Optional(t.Array(VideoCard, { maxItems: 8 })),
  episodes: t.Optional(t.Array(VideoCard)),
  description: t.String(),
  tags: t.Array(Tag),
  links: t.Array(GalleryCard),
  saved: t.Boolean(),
  progress: nullable(VideoProgress),

  state: t.Optional(VideoState),
  sourceName: t.Optional(t.String()),
  sourceBytes: t.Optional(t.Integer()),
  storedBytes: t.Optional(t.Integer()),

  keyframeIntervalMs: t.Optional(nullable(t.Integer())),
  isOwner: t.Boolean(),
};

export const VideoDetailOpen = closed({
  ...VideoCardOpen.properties,
  ...detailProps,
  playlist: t.Optional(t.String()),
});

export const VideoDetailBlocked = closed({ ...VideoCardBlocked.properties, ...detailProps });

export const VideoDetail = t.Union([VideoDetailOpen, VideoDetailBlocked]);

export type VideoDetailType = Static<typeof VideoDetail>;

const seriesProps = {
  id: Uuid,
  title: t.String(),
  altTitle: nullable(t.String()),
  owner: VideoPerson,
  episodeCount: t.Integer(),
  totalDurationMs: t.Integer(),
};

export const VideoSeriesOpen = closed({
  ...seriesProps,
  policy: t.Union([PolicyNone, PolicyWarn]),
  keyVisual: nullable(t.String()),
});

export const VideoSeriesBlocked = closed({ ...seriesProps, policy: PolicyBlock });

export const VideoSeries = t.Union([VideoSeriesOpen, VideoSeriesBlocked]);

export type VideoSeriesType = Static<typeof VideoSeries>;

const seriesDetailProps = {
  tags: t.Array(Tag),
  viewCount: t.Integer(),
  resolutions: t.Array(t.Integer()),
  createdAt: IsoDateTime,
  description: t.String(),
  episodes: t.Array(VideoDetail),
  links: t.Array(GalleryCard),
  isOwner: t.Boolean(),
};

export const VideoSeriesDetail = t.Union([
  closed({ ...VideoSeriesOpen.properties, ...seriesDetailProps }),
  closed({ ...VideoSeriesBlocked.properties, ...seriesDetailProps }),
]);

export type VideoSeriesDetailType = Static<typeof VideoSeriesDetail>;

export const VideoListQuery = t.Object({
  ...CursorQuery.properties,
  sort: t.Optional(literals(["new", "popular", "views"] as const)),
  q: t.Optional(t.String({ maxLength: 200 })),
  tag: t.Optional(ListParam()),
  xtag: t.Optional(ListParam()),
});

export const VideoInput = closed({
  title: t.String({ minLength: 1, maxLength: 120, pattern: "\\S" }),
  description: t.Optional(t.String({ maxLength: 2000 })),
  tags: t.Optional(t.Array(Tag, { maxItems: 30, uniqueItems: true })),
  seriesId: t.Optional(nullable(Uuid)),
  episodeNo: t.Optional(nullable(t.Integer({ minimum: 0, maximum: 999 }))),
  gids: t.Optional(t.Array(Gid, { maxItems: 5, uniqueItems: true })),
});

export const VideoCreate = closed({
  ...VideoInput.properties,
  fileName: t.String({ minLength: 1, maxLength: 255 }),
  sizeBytes: t.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
});

export const VideoUpdate = t.Partial(VideoInput, { additionalProperties: false });

export const VideoSeriesInput = closed({
  title: t.String({ minLength: 1, maxLength: 120, pattern: "\\S" }),
  altTitle: t.Optional(nullable(t.String({ maxLength: 120 }))),
  description: t.Optional(t.String({ maxLength: 2000 })),
});

export const VideoUpload = closed({
  videoId: Uuid,
  uploadUrl: t.String(),
  uploadToken: t.String(),
  chunkBytes: t.Integer(),
  expiresAt: IsoDateTime,
});

export const VideoMine = closed({
  items: t.Array(VideoDetail),
  nextCursor: nullable(t.String()),
  storedBytes: t.Integer(),
  processing: closed({ count: t.Integer(), done: nullable(t.Integer()), total: nullable(t.Integer()) }),
});

export const VideoHome = closed({
  featured: t.Array(VideoDetail, { maxItems: 5 }),
  continueWatching: t.Array(VideoDetail),
  popular: t.Array(VideoCard),
  linked: t.Array(VideoDetail),
  series: t.Array(VideoSeries),
  newest: t.Array(VideoCard),
});

export const MediaServerStatus = nullable(
  closed({
    lastReportAt: IsoDateTime,
    queued: t.Integer(),
    activeVideoId: nullable(Uuid),
    diskFreeBytes: t.Integer(),
    version: t.String(),
  }),
);

export const VideoAdmin = closed({
  items: t.Array(VideoDetail),
  nextCursor: nullable(t.String()),
  storedBytes: t.Integer(),
  users: t.Array(closed({ userId: t.Integer(), name: t.String(), storedBytes: t.Integer() })),
  server: MediaServerStatus,
});

export const VideoReceived = closed({
  videoId: Uuid,
  bytes: t.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
});

export const VideoReport = closed({
  videoId: Uuid,
  stage: literals(["uploading", "probing", "packaging", "storing"] as const),
  done: nullable(t.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER })),
  total: nullable(t.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER })),
});

export const VideoComplete = closed({
  videoId: Uuid,
  durationMs: t.Integer({ minimum: 1, maximum: 2147483647 }),
  width: t.Integer({ minimum: 1, maximum: 65535 }),
  height: t.Integer({ minimum: 1, maximum: 65535 }),
  videoCodec: VideoCodec,
  audioCodec: nullable(t.Literal("aac")),
  renditions: t.Array(t.Literal("source"), { minItems: 1, maxItems: 1 }),
  storedBytes: t.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
  keyframeIntervalMs: t.Optional(t.Integer({ minimum: 0, maximum: 2147483647 })),
});

export const VideoFailure = closed({
  videoId: Uuid,
  code: t.String({ minLength: 1, maxLength: 80 }),
  message: t.String({ minLength: 1, maxLength: 1000 }),
});

export const VideoHeartbeat = closed({
  queued: t.Integer({ minimum: 0 }),
  activeVideoId: nullable(Uuid),
  diskFreeBytes: t.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
  version: t.String({ minLength: 1, maxLength: 100 }),
  localIds: t.Array(Uuid, { maxItems: 10000, uniqueItems: true }),
  sourceIds: t.Array(Uuid, { maxItems: 10000, uniqueItems: true }),
  activeIds: t.Array(Uuid, { maxItems: 10000, uniqueItems: true }),
  recover: t.Boolean(),
});

export const VideoHeartbeatResponse = closed({
  deleteIds: t.Array(Uuid),
  localCleanupIds: t.Array(Uuid),
  queueIds: t.Array(Uuid),
});

export type VideoHeartbeatResponseType = Static<typeof VideoHeartbeatResponse>;

const Id = t.Object({ id: Uuid });

const ack = t.Object({ ack: t.Optional(BoolParam) });

export const videoRoutes = withIds({
  "videos.validateTags": route({
    module: "videos",
    method: "POST",
    path: "/api/videos/validate-tags",
    access: "user",
    summary: "영상 입력 태그의 중앙 하드 차단 판정",
    body: t.Required(t.Pick(VideoInput, ["tags"])),
    responses: { 200: closed({ blockedTags: t.Array(Tag, { maxItems: 30 }) }) },
    errors: [],
  }),
  "videos.home": route({
    module: "videos",
    method: "GET",
    path: "/api/videos/home",
    access: "user",
    summary: "영상 home",
    responses: { 200: VideoHome },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.list": route({
    module: "videos",
    method: "GET",
    path: "/api/videos",
    access: "user",
    summary: "영상 list",
    query: VideoListQuery,
    responses: { 200: pageOf(VideoCard) },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.saved": route({
    module: "videos",
    method: "GET",
    path: "/api/videos/saved",
    access: "user",
    summary: "영상 saved",
    query: CursorQuery,
    responses: { 200: pageOf(VideoCard) },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.mine": route({
    module: "videos",
    method: "GET",
    path: "/api/videos/mine",
    access: "user",
    summary: "영상 mine",
    query: CursorQuery,
    responses: { 200: VideoMine },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.get": route({
    module: "videos",
    method: "GET",
    path: "/api/videos/:id",
    access: "user",
    summary: "영상 get",
    params: Id,
    query: ack,
    responses: { 200: VideoDetail },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.status": route({
    module: "videos",
    method: "GET",
    path: "/api/videos/:id/status",
    access: "user",
    summary: "영상 status",
    params: Id,
    responses: { 200: VideoState },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.view": route({
    module: "videos",
    method: "POST",
    path: "/api/videos/:id/view",
    access: "user",
    summary: "영상 view",
    params: Id,
    responses: { 200: Ok },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.progress": route({
    module: "videos",
    method: "PUT",
    path: "/api/videos/:id/progress",
    access: "user",
    summary: "영상 progress",
    params: Id,
    body: closed({ positionMs: t.Integer({ minimum: 0, maximum: 2147483647 }) }),
    responses: { 200: Ok },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.save": route({
    module: "videos",
    method: "PUT",
    path: "/api/videos/:id/saved",
    access: "user",
    summary: "영상 save",
    params: Id,
    responses: { 204: Empty },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.unsave": route({
    module: "videos",
    method: "DELETE",
    path: "/api/videos/:id/saved",
    access: "user",
    summary: "영상 unsave",
    params: Id,
    responses: { 204: Empty },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.create": route({
    module: "videos",
    method: "POST",
    path: "/api/videos",
    access: "user",
    summary: "영상 create",
    body: VideoCreate,
    responses: { 201: VideoUpload },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED", "VIDEO_TOO_LARGE"],
  }),

  "videos.update": route({
    module: "videos",
    method: "PATCH",
    path: "/api/videos/:id",
    access: "user",
    summary: "영상 update",
    params: Id,
    body: VideoUpdate,
    responses: { 200: VideoDetail },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.delete": route({
    module: "videos",
    method: "DELETE",
    path: "/api/videos/:id",
    access: "user",
    summary: "영상 delete",
    params: Id,
    responses: { 204: Empty },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.seriesList": route({
    module: "videos",
    method: "GET",
    path: "/api/video-series",
    access: "user",
    summary: "영상 seriesList",
    query: t.Object({ ...CursorQuery.properties, mine: t.Optional(BoolParam) }),
    responses: { 200: pageOf(VideoSeries) },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.seriesGet": route({
    module: "videos",
    method: "GET",
    path: "/api/video-series/:id",
    access: "user",
    summary: "영상 seriesGet",
    params: Id,
    responses: { 200: VideoSeriesDetail },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.seriesCreate": route({
    module: "videos",
    method: "POST",
    path: "/api/video-series",
    access: "user",
    summary: "영상 seriesCreate",
    body: VideoSeriesInput,
    responses: { 201: VideoSeriesDetail },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.seriesUpdate": route({
    module: "videos",
    method: "PATCH",
    path: "/api/video-series/:id",
    access: "user",
    summary: "영상 seriesUpdate",
    params: Id,
    body: t.Partial(VideoSeriesInput, { additionalProperties: false }),
    responses: { 200: VideoSeriesDetail },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.seriesDelete": route({
    module: "videos",
    method: "DELETE",
    path: "/api/video-series/:id",
    access: "user",
    summary: "영상 seriesDelete",
    params: Id,
    responses: { 204: Empty },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.seriesKeyVisual": route({
    module: "videos",
    method: "PUT",
    path: "/api/video-series/:id/key-visual",
    access: "user",
    summary: "영상 seriesKeyVisual",
    params: Id,
    responses: { 200: Ok },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.gallery": route({
    module: "videos",
    method: "GET",
    path: "/api/galleries/:gid/videos",
    access: "user",
    summary: "영상 gallery",
    params: t.Object({ gid: GidParam }),
    query: CursorQuery,
    responses: { 200: pageOf(VideoCard) },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.adminList": route({
    module: "videos",
    method: "GET",
    path: "/api/admin/videos",
    access: "admin",
    summary: "영상 adminList",
    query: t.Object({ ...CursorQuery.properties, status: t.Optional(VideoStatus) }),
    responses: { 200: VideoAdmin },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.adminRemove": route({
    module: "videos",
    method: "POST",
    path: "/api/admin/videos/:id/remove",
    access: "admin",
    summary: "영상 adminRemove",
    params: Id,
    body: closed({ reason: t.String({ minLength: 1, maxLength: 1000, pattern: "\\S" }) }),
    responses: { 200: Ok },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),

  "videos.adminTags": route({
    module: "videos",
    method: "PATCH",
    path: "/api/admin/videos/:id/tags",
    access: "admin",
    summary: "영상 adminTags",
    params: Id,
    body: closed({ tags: t.Array(Tag, { maxItems: 30, uniqueItems: true }) }),
    responses: { 200: VideoDetail },
    errors: ["NOT_FOUND", "CONFLICT", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED"],
  }),
});

export const videoInternalRoutes = withIds({
  "internal.videoReceived": route({
    module: "internal",
    method: "POST",
    path: "/internal/videos/received",
    access: "internal",
    summary: "영상 Received",
    body: VideoReceived,
    responses: { 200: Ok },
    errors: ["NOT_FOUND", "CONFLICT"],
  }),

  "internal.videoProgress": route({
    module: "internal",
    method: "POST",
    path: "/internal/videos/progress",
    access: "internal",
    summary: "영상 Progress",
    body: VideoReport,
    responses: { 200: closed({ continue: t.Boolean() }) },
    errors: ["NOT_FOUND", "CONFLICT"],
  }),

  "internal.videoComplete": route({
    module: "internal",
    method: "POST",
    path: "/internal/videos/complete",
    access: "internal",
    summary: "영상 Complete",
    body: VideoComplete,
    responses: { 200: Ok },
    errors: ["NOT_FOUND", "CONFLICT"],
  }),

  "internal.videoFail": route({
    module: "internal",
    method: "POST",
    path: "/internal/videos/fail",
    access: "internal",
    summary: "영상 Fail",
    body: VideoFailure,
    responses: { 200: Ok },
    errors: ["NOT_FOUND", "CONFLICT"],
  }),

  "internal.videoHeartbeat": route({
    module: "internal",
    method: "POST",
    path: "/internal/videos/heartbeat",
    access: "internal",
    summary: "영상 Heartbeat",
    body: VideoHeartbeat,
    responses: { 200: VideoHeartbeatResponse },
    errors: ["NOT_FOUND", "CONFLICT"],
  }),
});
